import React from "react";
import {
    AccountClass,
    AccountType,
} from "@common/constants/domains/accounting/accounts";
import { getAvailableAccountTypes } from "@common/constants/domains/accounting/accountTypeHelpers";
import { profitAndLossAccountClasses } from "@common/constants/domains/accounting/helpers";
import {
    NATIVE_INVOICE_ADJUSTMENT_DEFAULT_ACCOUNT_ROLES,
    NATIVE_INVOICE_ADJUSTMENT_DEFAULT_RECEIVABLES_ROLE,
} from "@common/constants/nativeInvoiceAdjustmentAccountRoles";
import { PREMIUM_FEATURES } from "@common/flatRateBilling";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { useLedgers } from "@api/ledger.api";
import { Checkbox } from "@design-system/Checkbox/Checkbox";
import { useAllowFeature } from "@hooks/useAllowFeature";
import { findPrimaryLedgerId } from "@lib/accountOverrides";
import {
    disableNativeInvoicePerLineAdjustment,
    enableNativeInvoicePerLineAdjustment,
    NativeInvoicePerLineKind,
    PER_LINE_CALC_FLAG,
    PER_LINE_DISPLAY_FLAG,
} from "./nativeInvoicePerLineAdjustment";
import { NativeInvoiceFormState } from "./nativeInvoiceFormTypes";
import { NativeInvoiceAdjustmentCoding } from "./nativeInvoiceAdjustmentCoding";
import { useNativeInvoiceFormContext } from "./NativeInvoiceFormContext";
import {
    ItemAdjustmentAccountCell,
    ItemAdjustmentClassCell,
} from "./ItemAdjustmentCodingCells";
import styles from "./InvoiceFieldSettingsPanel.module.scss";

const ITEM_SETTINGS: Array<{
    kind: NativeInvoicePerLineKind;
    name: string;
}> = [
    { kind: "tax", name: "Tax" },
    { kind: "discount", name: "Discount" },
    { kind: "deposit", name: "Deposit" },
];

const LIABILITY_ACCOUNT_TYPES = getAvailableAccountTypes([
    AccountClass.liabilities,
]);

const ADJUSTMENT_ACCOUNT_TYPES: Record<
    NativeInvoicePerLineKind,
    AccountType[]
> = {
    tax: LIABILITY_ACCOUNT_TYPES,
    discount: getAvailableAccountTypes([...profitAndLossAccountClasses]),
    deposit: LIABILITY_ACCOUNT_TYPES,
};

function isPerLineEnabled(
    kind: NativeInvoicePerLineKind,
    flags: NativeInvoiceFormState,
): boolean {
    return flags[PER_LINE_CALC_FLAG[kind]];
}

function isPerLineDisplayed(
    kind: NativeInvoicePerLineKind,
    flags: NativeInvoiceFormState,
): boolean {
    return flags[PER_LINE_DISPLAY_FLAG[kind]];
}

// Per-line Tax / Discount / Deposit settings, plus the account and class
// copied onto those lines. Display is only meaningful once calculation is
// per-line, so its checkbox stays disabled (and forced off) until the
// matching calculation checkbox is on.
export const ItemSettingsContent: React.FC<{ entityId?: number }> = ({
    entityId,
}) => {
    const { state, setState } = useNativeInvoiceFormContext();
    const { hasAccess: hasClassesAccess } = useAllowFeature(
        PREMIUM_FEATURES.CLASSES,
    );
    const { hasAccess: hasAccrualLedgerAccess } = useAllowFeature(
        PREMIUM_FEATURES.ACCRUAL_LEDGER,
    );
    const { data: ledgers } = useLedgers(entityId);
    const showReceivablesAccountColumn =
        hasAccrualLedgerAccess &&
        findPrimaryLedgerId(ledgers, AccountingLedgerBasis.accruals) != null;
    const hasLineReceivablesOverride = state.lines.some(
        (line) => line.receivablesAccountId != null,
    );
    const receivablesDefaultRole = hasLineReceivablesOverride
        ? undefined
        : NATIVE_INVOICE_ADJUSTMENT_DEFAULT_RECEIVABLES_ROLE;

    const setPerLineCalc = (
        kind: NativeInvoicePerLineKind,
        isEnabled: boolean,
    ) => {
        setState((prev) =>
            isEnabled
                ? enableNativeInvoicePerLineAdjustment({ state: prev, kind })
                : disableNativeInvoicePerLineAdjustment({ state: prev, kind }),
        );
    };

    const setPerLineDisplay = (
        kind: NativeInvoicePerLineKind,
        isDisplayed: boolean,
    ) => {
        setState((prev) => ({
            ...prev,
            [PER_LINE_DISPLAY_FLAG[kind]]: isDisplayed,
        }));
    };

    const setAdjustmentCoding = (
        kind: NativeInvoicePerLineKind,
        patch: Partial<NativeInvoiceAdjustmentCoding>,
    ) => {
        setState((prev) => ({
            ...prev,
            adjustmentSettings: {
                ...prev.adjustmentSettings,
                [kind]: { ...prev.adjustmentSettings[kind], ...patch },
            },
        }));
    };

    const stopRowToggle = (event: React.SyntheticEvent) => {
        event.stopPropagation();
    };

    return (
        <div className={styles.tableScroller}>
            <table className={styles.table}>
                <thead>
                    <tr>
                        <th className={styles.fieldColumn}>Field</th>
                        <th className={styles.includeColumn}>
                            Calculate per line item
                        </th>
                        <th className={styles.includeColumn}>
                            Display column per line item
                        </th>
                        <th className={styles.codingColumn}>Account</th>
                        {showReceivablesAccountColumn ? (
                            <th className={styles.codingColumn}>
                                Receivables account
                            </th>
                        ) : null}
                        {hasClassesAccess ? (
                            <th className={styles.classColumn}>Class</th>
                        ) : null}
                    </tr>
                </thead>
                <tbody>
                    {ITEM_SETTINGS.map((field) => {
                        const isEnabled = isPerLineEnabled(field.kind, state);
                        const isDisplayed = isPerLineDisplayed(
                            field.kind,
                            state,
                        );
                        const coding = state.adjustmentSettings[field.kind];
                        return (
                            <tr
                                key={field.kind}
                                className={styles.fieldRow}
                                onClick={() =>
                                    setPerLineCalc(field.kind, !isEnabled)
                                }
                            >
                                <td className={styles.fieldColumn}>
                                    {field.name}
                                </td>
                                <td className={styles.includeColumn}>
                                    <span
                                        className={styles.checkboxWrap}
                                        onClick={(event) =>
                                            event.stopPropagation()
                                        }
                                    >
                                        <Checkbox
                                            checked={isEnabled}
                                            aria-label={`Calculate ${field.name} per line item`}
                                            onCheckedChange={(checked) =>
                                                setPerLineCalc(
                                                    field.kind,
                                                    checked,
                                                )
                                            }
                                        />
                                    </span>
                                </td>
                                <td className={styles.includeColumn}>
                                    <span
                                        className={styles.checkboxWrap}
                                        onClick={(event) =>
                                            event.stopPropagation()
                                        }
                                    >
                                        <Checkbox
                                            checked={isEnabled && isDisplayed}
                                            disabled={!isEnabled}
                                            aria-label={`Display ${field.name} column per line item`}
                                            onCheckedChange={(checked) =>
                                                setPerLineDisplay(
                                                    field.kind,
                                                    checked,
                                                )
                                            }
                                        />
                                    </span>
                                </td>
                                <td
                                    className={styles.codingColumn}
                                    onClick={stopRowToggle}
                                    onMouseDown={stopRowToggle}
                                >
                                    {entityId != null ? (
                                        <ItemAdjustmentAccountCell
                                            entityId={entityId}
                                            accountId={coding.accountId}
                                            allowedAccountTypes={
                                                ADJUSTMENT_ACCOUNT_TYPES[
                                                    field.kind
                                                ]
                                            }
                                            defaultAccountRole={
                                                NATIVE_INVOICE_ADJUSTMENT_DEFAULT_ACCOUNT_ROLES[
                                                    field.kind
                                                ]
                                            }
                                            onChange={(accountId) =>
                                                setAdjustmentCoding(
                                                    field.kind,
                                                    { accountId },
                                                )
                                            }
                                        />
                                    ) : null}
                                </td>
                                {showReceivablesAccountColumn ? (
                                    <td
                                        className={styles.codingColumn}
                                        onClick={stopRowToggle}
                                        onMouseDown={stopRowToggle}
                                    >
                                        {entityId != null ? (
                                            <ItemAdjustmentAccountCell
                                                entityId={entityId}
                                                accountId={
                                                    coding.receivablesAccountId
                                                }
                                                allowedAccountTypes={[
                                                    AccountType.accountsReceivable,
                                                ]}
                                                defaultAccountRole={
                                                    receivablesDefaultRole
                                                }
                                                onChange={(
                                                    receivablesAccountId,
                                                ) =>
                                                    setAdjustmentCoding(
                                                        field.kind,
                                                        {
                                                            receivablesAccountId,
                                                        },
                                                    )
                                                }
                                            />
                                        ) : null}
                                    </td>
                                ) : null}
                                {hasClassesAccess ? (
                                    <td
                                        className={styles.classColumn}
                                        onClick={stopRowToggle}
                                        onMouseDown={stopRowToggle}
                                    >
                                        <ItemAdjustmentClassCell
                                            classIds={coding.classIds}
                                            onChange={(classIds) =>
                                                setAdjustmentCoding(
                                                    field.kind,
                                                    { classIds },
                                                )
                                            }
                                        />
                                    </td>
                                ) : null}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};
